import React, { useState } from 'react'
import { useField, useForm } from 'react-final-form'
import Recaptcha from 'react-recaptcha'
import filesize from 'filesize.js'
import { isEmpty, map } from 'lodash'
import { useFeature } from 'toggled'

import { TextField } from '~/components'
import Datepicker from '~/components/datepicker'
import { InputFile } from '~/components/input-file'
import { useLocationSelector } from '~/hooks'
import useCoverage from '~/hooks/use-coverage'
import useFormatters from '~/hooks/use-formatters'
import useVendor from '~/hooks/use-vendor'
import Anchor from '~/truck/anchor'
import Field from '~/truck/field'
import NewButton from '~/truck/new-button/NewButton'
import theme from '~/truck/theme'
import { allowedFileExtensions, fileSizeLimits, flags } from '~/utils/constants'

import AttachedFilePreview from './attached-file-preview'
import { Container, StyledGrid as Grid } from './elements'

const complainTexts = {
  claim: 'Disconformidad relacionada a los productos o servicios.',
  complaint: `
    Disconformidad no relacionada a los productos o servicios o malestar o
    descontento respecto a la atención al público. La formulación del reclamo
    no impide acudir a otras vías de solución de controversias ni es requisito
    previo para interponer una denuncia ante el INDECOPI.
  `,
}

const GReCaptcha = props => {
  const { sitekey, render, onloadCallback, onChange } = props

  return (
    <Recaptcha
      sitekey={sitekey}
      render={render}
      onloadCallback={onloadCallback}
      verifyCallback={res => onChange(res)}
    />
  )
}

function AddressFormFields({ coverageTree }) {
  const [divisions, onDivisionChange] = useLocationSelector(coverageTree)

  return (
    <>
      {map(divisions, (division, index) => {
        const { name, localities, fieldName, fieldValue } = division

        return (
          <Grid.Column key={name} sm={12} lg={6} marginToRight={index % 2 === 0}>
            <TextField
              label={name}
              prompt="Seleccione"
              as="select"
              name={fieldName}
              value={fieldValue}
              onChange={e => onDivisionChange(e.target.value, fieldName)}
              disabled={isEmpty(localities)}
            >
              {map(localities, locality => {
                return (
                  <option key={locality.id} value={locality.id}>
                    {locality.name}
                  </option>
                )
              })}
            </TextField>
          </Grid.Column>
        )
      })}
      <Grid.Column sm={12} lg={6}>
        <TextField name="addressLine" label="Dirección" />
      </Grid.Column>
    </>
  )
}

const Form = props => {
  const { handleSubmit, values, submitting, style, className } = props

  const claimedAtField = useField('claimedAt')

  const { country, name: vendorName, privacyPolicyUrl } = useVendor()

  const { currencySymbol } = country

  const { loading: loadingCoverage, coverageTree } = useCoverage()

  const [datepickerIsOpen, setDatepickerIsOpen] = useState(false)

  const formatters = useFormatters()

  const form = useForm()

  const fileSizeHumanFormat = filesize(fileSizeLimits.CUSTOMER_COMPLAINT_ATTACHMENT, 0)

  const customerComplaintsFF = useFeature(flags.CUSTOMER_COMPLAINTS)

  const purchaseChannels = customerComplaintsFF?.settings?.purchaseChannels

  const canAttachFile = customerComplaintsFF?.settings?.enableAttachment ?? false

  const onDateChange = date => {
    claimedAtField.input.onChange(date.toISOString())

    setDatepickerIsOpen(false)
  }

  return (
    <Container style={style} className={className}>
      <h3 className="text-3xl font-bold">Libro de Reclamaciones</h3>
      <h4 className="text-xl font-semibold">1. Identificación del Consumidor Reclamante</h4>
      <Grid>
        <Grid.Column sm={12} lg={6} marginToRight>
          <TextField name="firstName" label="Nombres" />
        </Grid.Column>
        <Grid.Column sm={12} lg={6}>
          <TextField name="lastName" label="Apellidos" />
        </Grid.Column>
        <Grid.Column sm={12} lg={6} marginToRight>
          <TextField name="email" label="Correo" />
        </Grid.Column>
        <Grid.Column sm={12} lg={6}>
          <TextField name="phone" label="Teléfono" />
        </Grid.Column>
        <Grid.Column sm={12} lg={6} marginToRight>
          <TextField name="documentType" label="Tipo de documento" as="select" prompt="Seleccione">
            <option value="dni">DNI</option>
            <option value="ce">CE</option>
          </TextField>
        </Grid.Column>
        <Grid.Column sm={12} lg={6}>
          <TextField name="documentNumber" label="Número de documento" />
        </Grid.Column>
        <Grid.Column sm={12}>
          <Field name="isUnderage" as="checkbox">
            ¿Es menor de edad?
          </Field>
        </Grid.Column>
        {values.isUnderage && (
          <>
            <Grid.Column sm={12} lg={6} marginToRight>
              <TextField name="parentFirstName" label="Nombres del apoderado" disabled={!values.isUnderage} />
            </Grid.Column>
            <Grid.Column sm={12} lg={6}>
              <TextField name="parentLastName" label="Apellidos del apoderado" disabled={!values.isUnderage} />
            </Grid.Column>
            <Grid.Column sm={12} lg={6} marginToRight>
              <TextField name="parentEmail" label="Correo del apoderado" disabled={!values.isUnderage} />
            </Grid.Column>
            <Grid.Column sm={12} lg={6}>
              <TextField name="parentPhone" label="Teléfono del apoderado" disabled={!values.isUnderage} />
            </Grid.Column>
            <Grid.Column sm={12} lg={6} marginToRight>
              <TextField
                name="parentDocumentType"
                label="Tipo de documento del apoderado"
                as="select"
                prompt="Seleccione"
                disabled={!values.isUnderage}
              >
                <option value="dni">DNI</option>
                <option value="ce">CE</option>
              </TextField>
            </Grid.Column>
            <Grid.Column sm={12} lg={6}>
              <TextField
                name="parentDocumentNumber"
                label="Número de documento del apoderado"
                disabled={!values.isUnderage}
              />
            </Grid.Column>
          </>
        )}
        {!loadingCoverage && <AddressFormFields coverageTree={coverageTree} />}
      </Grid>
      <h4 className="text-xl font-semibold">2. Identificación del Bien Contratado</h4>
      <Grid>
        <Grid.Column sm={6} lg={2}>
          <Field as="radio" name="assetType" value="product" id="p-radio">
            Producto
          </Field>
        </Grid.Column>
        <Grid.Column sm={6} lg={10}>
          <Field as="radio" name="assetType" value="service" id="s-radio">
            Servicio
          </Field>
        </Grid.Column>
        {purchaseChannels && (
          <Grid.Column sm={12} lg={6} marginToRight>
            <TextField name="purchaseChannel" label="Medio de compra" as="select" prompt="Seleccione">
              {map(purchaseChannels, purchaseChannel => (
                <option value={purchaseChannel} key={purchaseChannel}>
                  {purchaseChannel}
                </option>
              ))}
            </TextField>
          </Grid.Column>
        )}
        <Grid.Column sm={12} lg={6} marginToRight>
          <TextField
            readOnly
            name="claimedAt"
            label="Fecha"
            format={formatters.date}
            autoComplete="off"
            onFocus={() => setDatepickerIsOpen(true)}
          />
        </Grid.Column>
        <Grid.Column sm={12} lg={6} style={{ flexGrow: purchaseChannels ? 0 : 1 }}>
          <TextField
            name="claimedAmount"
            label="Monto reclamado"
            placeholder="00.00"
            renderAdornment={<span>{currencySymbol}</span>}
            type="number"
          />
        </Grid.Column>
        <Grid.Column sm={12}>
          <TextField as="textarea" name="assetDescription" label="Descripción" />
        </Grid.Column>
      </Grid>
      <h4 className="text-xl font-semibold">3. Detalle de la Reclamación y Pedido del Consumidor</h4>
      <Grid>
        <Grid.Column sm={6} lg={2}>
          <Field as="radio" name="complainType" value="claim" id="c-radio">
            Reclamo
          </Field>
        </Grid.Column>
        <Grid.Column sm={6} lg={10}>
          <Field as="radio" name="complainType" value="complaint" id="r-radio">
            Queja
          </Field>
        </Grid.Column>
        <Grid.Column sm={12}>
          <div style={{ borderRadius: 4, padding: 16, display: 'flex', backgroundColor: theme.colors.grayLight }}>
            <p>{complainTexts[values.complainType]}</p>
          </div>
        </Grid.Column>
        <Grid.Column sm={12}>
          <TextField as="textarea" name="details" label="Detalle" />
        </Grid.Column>
        <Grid.Column sm={12}>
          <TextField as="textarea" name="customerRequest" label="Pedido del reclamante" />
        </Grid.Column>
        {canAttachFile && (
          <Grid.Column sm={6} lg={12}>
            <InputFile
              sizeLimit={fileSizeLimits.CUSTOMER_COMPLAINT_ATTACHMENT}
              accept={allowedFileExtensions.CUSTOMER_COMPLAINT_ATTACHMENT}
              onChange={e => {
                form.change('fileAttachment', e.target.files[0])
              }}
              style={{
                maxWidth: '50%',
              }}
              description={`JPG, PNG o PDF tamaño máx. de ${fileSizeHumanFormat}`}
            />
            <AttachedFilePreview />
          </Grid.Column>
        )}
        <Grid.Column sm={12}>
          <Field as="checkbox" name="termsAccepted">
            Me encuentro conforme con los términos de mi reclamo o queja
          </Field>
        </Grid.Column>
        {privacyPolicyUrl && (
          <Grid.Column sm={12}>
            <Field as="checkbox" name="privacyPolicyAccepted">
              {'Declaro que he leído y acepto la '}
              <Anchor href="/politica-de-privacidad" target="_blank" $color="info">
                Política de Privacidad
              </Anchor>
              {` de ${vendorName}`}
            </Field>
          </Grid.Column>
        )}
        <Grid.Column sm={12}>
          <p className="mb-8 mt-4">
            El proveedor deberá dar respuesta al reclamo en un plazo no mayor de quince (15) días hábiles.
          </p>
        </Grid.Column>
      </Grid>
      <Field
        name="reCaptchaToken"
        component={GReCaptcha}
        style={{ marginBottom: theme.spacing.cozy * 8 }}
        sitekey={process.env.RE_CAPTCHA_SITE_KEY}
        render="explicit"
        onloadCallback={() => null}
      />
      <NewButton type="button" onClick={handleSubmit} loading={submitting}>
        Enviar
      </NewButton>
      <Datepicker
        isOpen={datepickerIsOpen}
        onClose={() => setDatepickerIsOpen(false)}
        value={claimedAtField.input.value}
        onChange={onDateChange}
      />
    </Container>
  )
}

export default Form
